/**
 * LP de venda do produto low ticket "Roteiro Prático de Avaliação
 * Musculoesquelética".
 *
 * Versão clara (identidade do e-book: off-white/cinza + âmbar FEO nos CTAs),
 * com a estrutura de dobras revisada pelo time de marketing.
 *
 * Teste A/B de posição do vídeo:
 * - videoPlacement="hero" → /roteiro-avaliacao-1 (vídeo já na hero)
 * - videoPlacement="meio" → /roteiro-avaliacao-2 (imagem na hero + vídeo no meio)
 *
 * Checkout Eduzz direto, sem captura de lead.
 */
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Brain,
  Check,
  ClipboardList,
  GraduationCap,
  ListChecks,
  MessageSquareWarning,
  Play,
  PlayCircle,
  Quote,
  ShieldCheck,
  Sparkles,
  Star,
  Stethoscope,
  Users,
} from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";


import { useOfferCheckoutUrl } from "@/lib/checkout/use-offer-checkout-url";
import { DEPOIMENTOS } from "@/lib/depoimentos/data";

import logoFeo from "@/assets/feo/brand/logo-horizontal-colorido-escuro.webp.asset.json";
import heroClara from "@/assets/roteiro/hero-clara.jpg.asset.json";
import mockupCelular from "@/assets/roteiro/mockup-celular.jpg.asset.json";
import duvidasFisio from "@/assets/roteiro/duvidas-fisio.jpg.asset.json";
import bonusComunidade from "@/assets/roteiro/bonus-comunidade.jpg.asset.json";
import videoThumb from "@/assets/roteiro/video-thumb.jpg.asset.json";
import profFuku from "@/assets/roteiro/prof-fuku.webp.asset.json";
import profRafa from "@/assets/roteiro/prof-rafa.webp.asset.json";
import profGui from "@/assets/roteiro/prof-gui.webp.asset.json";

const CHECKOUT_URL = "https://chk.eduzz.com/KW8KE4RR01?cupom=VIP&pf=1";

export const PRECO_DE = "R$97,00";
export const PRECO_POR = "R$37,00";


/** Dobra 3 — dúvidas reais que aparecem durante a avaliação. */
const DUVIDAS = [
  "Será que eu perguntei tudo que precisava?",
  "Por onde eu começo essa avaliação?",
  "Quais testes fazem sentido para esse caso?",
  "Será que estou deixando passar alguma coisa?",
];

/** Dobra 4 — o que a pessoa recebe ao garantir o acesso. */
const ENTREGAS = [
  {
    icon: BookOpen,
    titulo: "Roteiro Prático",
    texto:
      "Um material organizado para acompanhar sua prática e ajudar você a estruturar as diferentes etapas de uma avaliação musculoesquelética.",
  },
  {
    icon: PlayCircle,
    titulo: "+5 aulas exclusivas",
    texto:
      "Conteúdos complementares para aprofundar os principais pontos abordados no Roteiro e aproximá-los ainda mais da prática clínica.",
  },
  {
    icon: GraduationCap,
    titulo: "Certificado 5 horas",
    texto:
      "Ao concluir o conteúdo, você também recebe seu certificado. Você pode estudar o material do início ao fim e depois voltar a ele sempre que precisar.",
  },
];

/** Dobra 5 — para quem é. */
const PARA_QUEM = [
  "Atende pacientes com condições musculoesqueléticas",
  "Sente que às vezes conduz a avaliação “no automático”",
  "Fica em dúvida sobre o que perguntar durante a anamnese",
  "Quer organizar melhor seu exame físico",
  "Tem medo de deixar passar informações importantes",
  "Quer conectar melhor os achados da avaliação às suas decisões clínicas",
  "Procura um material prático para consultar durante sua rotina",
];

const APRENDIZADOS = [
  { icon: ListChecks, text: "Como **estruturar uma avaliação MSK do início ao fim, sem se perder**" },
  { icon: Stethoscope, text: "Como sair de métodos engessados e **avaliar com mais assertividade**" },
  { icon: ClipboardList, text: "Quais informações são essenciais na **anamnese clínica**" },
  { icon: ShieldCheck, text: "Como conduzir uma **avaliação mais segura e centrada no paciente**" },
  { icon: Brain, text: "Como **organizar achados clínicos e transformá-los em tomada de decisão**" },
  { icon: MessageSquareWarning, text: "Os erros mais comuns na **avaliação MSK e como evitá-los**" },
];

/** Dobra de oferta — itens inclusos. */
const OFERTA_ITENS = [
  {
    titulo: "Roteiro Prático de Avaliação Musculoesquelética",
    texto: "Um material para consultar antes, durante ou depois dos seus atendimentos.",
  },
  {
    titulo: "+5 aulas exclusivas",
    texto: "Conteúdos para complementar o Roteiro e aprofundar pontos importantes da avaliação musculoesquelética.",
  },
  { titulo: "Certificado", texto: "Conclua o conteúdo e receba seu certificado." },
  {
    titulo: "1 ano de Comunidade FEO (BÔNUS)",
    texto: "Um espaço para trocar experiências e continuar aprendendo com outros profissionais.",
  },
];

const PROFESSORES = [
  {
    nome: "Leandro Fukusawa",
    foto: profFuku.url,
    bio: "Fisioterapeuta com ampla experiência clínica nas áreas de ortopedia, traumatologia e esporte. É especialista em reabilitação musculoesquelética pela Faculdade de Ciências Médicas Santa Casa de São Paulo (FCMSC-SP), mestre em Fisioterapia pela Universidade Cidade de São Paulo (UNICID) e doutor pelo programa de Ciências da Saúde pela FCMSC-SP.",
  },
  {
    nome: "Rafael Alaiti",
    foto: profRafa.url,
    bio: "Fisioterapeuta com ampla experiência clínica no manejo de dor crônica e terapia Musculoesquelética pelo Hospital das Clínicas da Faculdade de Medicina da USP (HC-FMUSP), mestre e doutor em Neurociência da Dor pelo programa de Neurociências e Comportamento da USP e MBA em Health Tech pela FIAP.",
  },
  {
    nome: "Guilherme Paiva",
    foto: profGui.url,
    bio: "Fisioterapeuta com ampla experiência clínica na área de ortopedia, dor e traumatologia. É pós-graduado em Fisioterapia Traumato-ortopédica pelo Centro Universitário Barão de Mauá (Ribeirão Preto/SP), mestre em Ciências pela Faculdade de Medicina de Ribeirão Preto da USP (FMRP-USP) e possui aprimoramento em Ortopedia e Traumatologia pelo Hospital das Clínicas da FMRP-USP.",
  },
];

const DEPOIMENTOS_LP = DEPOIMENTOS.filter((d) => d.comment.length > 80 && d.comment.length < 340).slice(0, 6);

export const FAQ = [
  {
    q: "Como terei acesso ao roteiro?",
    a: "Após o pagamento, você recebe no seu e-mail um login e senha para a área de membros, com o seu exemplar do e-book e as 5 aulas do professor Leandro Fukusawa.",
  },
  {
    q: "As aulas ficam gravadas ou são ao vivo?",
    a: "As 5 aulas são 100% gravadas, permitindo que você assista no seu ritmo, quando e onde quiser.",
  },
  {
    q: "Tem certificado?",
    a: "Sim. Ao concluir as 5 aulas você recebe um certificado de 5 horas. As aulas funcionam como um treinamento em avaliação musculoesquelética.",
  },
  {
    q: "O que é a Comunidade FEO?",
    a: "É um espaço exclusivo para fisioterapeutas trocarem experiências, discutirem casos, compartilharem dúvidas e continuarem aprendendo juntos. Comprando hoje, você leva 1 ano de acesso como bônus.",
  },
  {
    q: "Posso tirar dúvidas na plataforma?",
    a: "Sim. Dentro do ambiente de cada videoaula você pode fazer anotações e enviar as suas dúvidas. Nossos professores respondem com frequência.",
  },
  {
    q: "É um pagamento único?",
    a: "Sim. São R$37,00 em um pagamento único, sem mensalidade e sem renovação.",
  },
];

/** Renderiza **negrito** simples da copy original. */
function Rich({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold text-foreground">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function Cta({ label, href, className = "" }: { label: string; href: string; className?: string }) {
  return (
    <a
      href={href}
      className={`group inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-center text-sm font-bold uppercase tracking-wide text-primary-foreground shadow-lg transition hover:brightness-105 sm:text-base ${className}`}
    >
      {label}
      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
    </a>
  );
}

function SectionTitle({ children, kicker }: { children: React.ReactNode; kicker?: string }) {
  return (
    <div className="text-center">
      {kicker && (
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{kicker}</p>
      )}
      <h2 className="mx-auto mt-3 max-w-3xl text-balance text-2xl font-bold leading-snug text-foreground sm:text-4xl">
        {children}
      </h2>
    </div>
  );
}

/**
 * Player do vídeo do produto. Enquanto o vídeo definitivo não estiver pronto,
 * mostra uma thumb provisória — basta trocar por um embed depois.
 */
function VideoBlock({ eager = false }: { eager?: boolean }) {
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-3xl border border-border bg-secondary shadow-xl">
      <img
        src={videoThumb.url}
        alt="Vídeo de apresentação do Roteiro Prático de Avaliação Musculoesquelética"
        className="absolute inset-0 h-full w-full object-cover"
        width={1280}
        height={720}
        loading={eager ? "eager" : "lazy"}
      />
      <div className="absolute inset-0 bg-foreground/25" aria-hidden />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
        <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary shadow-lg">
          <Play className="h-7 w-7 translate-x-[1px] fill-primary-foreground text-primary-foreground" />
        </span>
        <span className="rounded-full bg-card/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Vídeo em breve
        </span>
      </div>
    </div>
  );
}

export function RoteiroAvaliacaoLP({
  videoPlacement,
}: {
  videoPlacement: "hero" | "meio";
}) {
  const checkoutUrl = useOfferCheckoutUrl(CHECKOUT_URL);
  const videoNoHero = videoPlacement === "hero";

  return (
    <main className="roteiro-scope min-h-screen">


      {/* DOBRA 1 — HERO */}
      <section className="border-b border-border bg-secondary/40">

        <div className="mx-auto max-w-6xl px-5 py-10 sm:py-14">
          <img decoding="async" src={logoFeo.url} alt="Portal Fisio em Ortopedia" className="h-9 w-auto sm:h-10" />

          <div className="mt-10 grid items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground">
                <Sparkles className="h-3.5 w-3.5" /> Roteiro Prático
              </p>
              <h1 className="mt-5 text-balance text-3xl font-bold leading-tight text-foreground sm:text-5xl">
                Da anamnese ao tratamento: organize sua avaliação musculoesquelética em 5 etapas.
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                Um guia prático para fisioterapeutas que querem saber o que investigar, quais informações realmente
                importam e como organizar os achados durante uma avaliação.
              </p>
              <div className="mt-7 flex flex-col items-start gap-3">
                <Cta href={checkoutUrl} label="Quero avaliar com mais segurança" />
                <p className="text-sm text-muted-foreground">
                  De <span className="line-through">{PRECO_DE}</span> por{" "}
                  <span className="font-bold text-foreground">{PRECO_POR}</span> · pagamento único
                </p>
              </div>
            </div>

            <div>
              {videoNoHero ? (
                <VideoBlock eager />
              ) : (
                <img
                  src={heroClara.url}
                  alt="Roteiro Prático de Avaliação Musculoesquelética impresso e em versão digital"
                  className="w-full rounded-3xl border border-border bg-card object-cover shadow-xl"
                  width={1408}
                  height={1104}
                  loading="eager"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* DOBRA 2 — PROVA SOCIAL */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <SectionTitle kicker="+10 mil exemplares vendidos">
          Não bastava criar um roteiro de avaliação. Ele precisava ter o padrão FEO.
        </SectionTitle>
        <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-muted-foreground">
          Isso significa reunir evidência, experiência clínica e aplicabilidade em um material que realmente faça
          sentido quando existe um paciente na sua frente. É assim que construímos nossos conteúdos, e é assim que
          nossos alunos percebem a FEO:
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {DEPOIMENTOS_LP.map((d) => (
            <figure key={d.id} className="rounded-3xl border border-border bg-card p-6 shadow-sm">
              <div className="flex gap-0.5 text-primary" aria-label={`${d.rating} de 5 estrelas`}>
                {Array.from({ length: d.rating }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <blockquote className="mt-4 text-sm leading-relaxed text-muted-foreground">“{d.comment}”</blockquote>
              <figcaption className="mt-4 text-sm font-semibold text-foreground">{d.author_name}</figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-6 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Avaliações reais do Google
        </p>
      </section>

      {/* DOBRA 3 — DÚVIDAS DO FISIO */}
      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="mx-auto max-w-6xl px-5">
          <SectionTitle>Um passo a passo que vai mudar a sua forma de avaliar na próxima consulta</SectionTitle>

          <div className="mt-10 grid items-center gap-10 lg:grid-cols-2">
            <ul className="space-y-3">
              {DUVIDAS.map((frase) => (
                <li
                  key={frase}
                  className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm"
                >
                  <Quote className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="text-base font-medium text-foreground">“{frase}”</span>
                </li>
              ))}
            </ul>

            <div>
              <img
                src={duvidasFisio.url}
                alt="Fisioterapeuta conduzindo a avaliação de um paciente com prancheta em mãos"
                className="w-full rounded-3xl border border-border object-cover shadow-lg"
                width={1200}
                height={1008}
                loading="lazy"
              />
            </div>
          </div>

          <div className="mx-auto mt-10 max-w-3xl space-y-3 text-center text-base leading-relaxed text-muted-foreground">
            <p className="font-semibold text-foreground">
              Um roteiro para consultar antes, durante ou depois dos seus atendimentos.
            </p>
            <p>
              Ele foi criado para ajudar você a organizar sua avaliação MSK, reunindo em um só lugar os principais
              pontos que podem ser investigados e avaliados ao longo do atendimento.
            </p>
          </div>
        </div>
      </section>

      {/* VÍDEO NO MEIO (variante B) */}
      {!videoNoHero && (
        <section className="mx-auto max-w-4xl px-5 py-16">
          <SectionTitle>Antes de continuar, vem conhecer o Roteiro por dentro.</SectionTitle>
          <p className="mx-auto mt-4 max-w-xl text-center text-base text-muted-foreground">
            Dê o play. A gente te mostra o material por dentro e como você pode consultá-lo no dia a dia clínico.
          </p>
          <div className="mt-8">
            <VideoBlock />
          </div>
        </section>
      )}

      {/* DOBRA 4 — O QUE VOCÊ RECEBE */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <SectionTitle>Ao garantir seu acesso, você recebe:</SectionTitle>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {ENTREGAS.map((item) => (
            <article key={item.titulo} className="rounded-3xl border border-border bg-card p-6 shadow-sm">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                <item.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-bold text-foreground">{item.titulo}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.texto}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Cta href={checkoutUrl} label="Garantir o meu Roteiro Prático" />
        </div>
      </section>

      {/* DOBRA 5 — PARA QUEM É */}
      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="mx-auto max-w-3xl px-5">
          <SectionTitle>O Roteiro Prático é para você que...</SectionTitle>
          <ul className="mt-10 space-y-3">
            {PARA_QUEM.map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-3 w-3" />
                </span>
                <span className="text-base text-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DOBRA 6 — O QUE VAI APRENDER */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <SectionTitle>O que você vai aprender:</SectionTitle>

        <div className="mt-10 grid items-center gap-6 lg:grid-cols-[1fr_auto_1fr]">
          <div className="space-y-4">
            {APRENDIZADOS.slice(0, 3).map((item) => (
              <div key={item.text} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <item.icon className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  <Rich text={item.text} />
                </p>
              </div>
            ))}
          </div>

          <div className="order-first mx-auto w-full max-w-[280px] lg:order-none">
            <img
              src={mockupCelular.url}
              alt="Roteiro Prático de Avaliação Musculoesquelética aberto no celular"
              className="w-full rounded-3xl object-cover"
              width={912}
              height={1200}
              loading="lazy"
            />
          </div>

          <div className="space-y-4">
            {APRENDIZADOS.slice(3).map((item) => (
              <div key={item.text} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <item.icon className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  <Rich text={item.text} />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOBRA 7 — BÔNUS COMUNIDADE */}
      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 lg:grid-cols-2">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary-foreground">
              <Users className="h-3.5 w-3.5" /> Bônus
            </p>
            <h2 className="mt-4 text-2xl font-bold leading-snug text-foreground sm:text-4xl">
              Comprando hoje, você também leva um BÔNUS especial: acesso à Comunidade FEO
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Fisioterapia não se constrói sozinho. Por isso, além do Roteiro Prático, você também terá acesso à
              Comunidade FEO: um espaço exclusivo para fisioterapeutas trocarem experiências, discutirem casos,
              compartilharem dúvidas e continuarem aprendendo juntos.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Uma comunidade para levar as discussões da fisioterapia para além dos cursos e para mais perto da
              prática.
            </p>
            <p className="mt-5 text-sm font-bold uppercase tracking-wider text-foreground">Valor: R$ 0</p>
          </div>

          <img
            src={bonusComunidade.url}
            alt="Ilustração da Comunidade FEO de fisioterapeutas"
            className="w-full rounded-3xl border border-border bg-card object-cover shadow-sm"
            width={1200}
            height={900}
            loading="lazy"
          />
        </div>
      </section>

      {/* DOBRA 8 — OFERTA */}
      <section className="mx-auto max-w-4xl px-5 py-16">
        <SectionTitle kicker="Investimento">
          Tenha em mãos um roteiro para consultar no seu próximo atendimento por {PRECO_POR}
        </SectionTitle>

        <div className="mt-10 rounded-3xl border border-border bg-card p-6 shadow-lg sm:p-10">
          <ul className="space-y-5">

            {OFERTA_ITENS.map((item) => (
              <li key={item.titulo} className="flex items-start gap-3">
                <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="font-bold text-foreground">{item.titulo}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.texto}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-8 border-t border-border pt-8 text-center">
            <p className="text-sm text-muted-foreground">
              De <span className="line-through">{PRECO_DE}</span>
            </p>
            <p className="mt-1 text-5xl font-extrabold text-foreground">{PRECO_POR}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              Pagamento único · acesso imediato na área de membros
            </p>
            <div className="mt-6">
              <Cta href={checkoutUrl} label="Quero meu Roteiro Prático" />
            </div>
          </div>
        </div>
      </section>

      {/* DOBRA 9 — PROFESSORES */}
      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="mx-auto max-w-6xl px-5">
          <SectionTitle>As mentes por trás do Roteiro Prático</SectionTitle>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {PROFESSORES.map((p) => (
              <article key={p.nome} className="rounded-3xl border border-border bg-card p-6 text-center shadow-sm">
                <img
                  src={p.foto}
                  alt={`Foto do professor ${p.nome}`}
                  className="mx-auto h-28 w-28 rounded-full object-cover"
                  loading="lazy"
                />
                <h3 className="mt-4 text-lg font-bold text-foreground">{p.nome}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.bio}</p>
              </article>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Cta href={checkoutUrl} label={`Garanta seu roteiro por ${PRECO_POR}`} />
          </div>
        </div>
      </section>

      {/* DOBRA 10 — FAQ */}
      <section className="mx-auto max-w-3xl px-5 py-16">
        <SectionTitle>Perguntas frequentes</SectionTitle>
        <Accordion type="single" collapsible className="mt-8">
          {FAQ.map((item, i) => (
            <AccordionItem key={item.q} value={`faq-${i}`} className="border-border">
              <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA FINAL */}
      <section className="border-t border-border bg-secondary/40 py-16">
        <div className="mx-auto max-w-2xl px-5 text-center">
          <h2 className="text-2xl font-bold text-foreground sm:text-3xl">
            Comece a avaliar com mais clareza já na próxima consulta
          </h2>
          <p className="mt-3 text-muted-foreground">
            Roteiro + 5 aulas + certificado de 5 horas + 1 ano de Comunidade FEO por{" "}
            <span className="font-bold text-foreground">{PRECO_POR}</span> (de {PRECO_DE}).
          </p>
          <div className="mt-7">
            <Cta href={checkoutUrl} label={`Garanta seu Roteiro por ${PRECO_POR}`} />
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-10 text-center">
        <img src={logoFeo.url} alt="Portal Fisio em Ortopedia" className="mx-auto h-8 w-auto opacity-80" loading="lazy" decoding="async" />
        <p className="mt-4 text-xs text-muted-foreground">
          Portal Fisio em Ortopedia · Todos os direitos reservados © 2026
        </p>
      </footer>
    </main>
  );
}
